import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import { notify } from "../../../feedback/SnackbarProvider";
import { Button } from "../../Button/Button";
import { Form } from "../../Form/Form";
import { Input } from "../../Input/Input";
import { InputNumber } from "../../Input/InputNumber";
import { List } from "../../List/List";
import { Modal } from "../../Modal/Modal";
import { Popconfirm } from "../../Overlay/Overlay";
import { Select } from "../../Select/Select";
import { Switch } from "../../Switch/Switch";
import { Tag } from "../../Tag/Tag";
import { Tooltip } from "../../Tooltip/Tooltip";
import { Typography } from "../../Typography/Typography";
import {
  ArrowRight as CaretRightOutlined,
  DeleteOutline as DeleteOutlined,
  EditOutlined as EditOutlined,
  Add as PlusOutlined,
} from "@mui/icons-material";
import {
  MY_ACTIVE_CHAT_SKILLS,
  MY_CHAT_SKILL_SCHEDULES,
  ADD_CHAT_SKILL_SCHEDULE,
  UPDATE_CHAT_SKILL_SCHEDULE,
  DELETE_CHAT_SKILL_SCHEDULE,
  RUN_CHAT_SKILL_SCHEDULE_NOW,
} from "../graphql";
import {
  ChatSkillScheduleFrequency,
  ChatSkillScheduleRunStatus,
} from "_graphql-types-frontend/graphql";

const { Text } = Typography;

type ScheduleRow = {
  id: number;
  chatSkillId: number;
  frequency: ChatSkillScheduleFrequency;
  runAtHour: number;
  runAtMinute: number;
  runOnWeekday: number | null;
  runOnDayOfMonth: number | null;
  timezone: string;
  extraInstructions: string | null;
  enabled: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastRunStatus: ChatSkillScheduleRunStatus | null;
  lastConversationId: number | null;
};

type ScheduleFormValues = {
  chatSkillId: number;
  frequency: ChatSkillScheduleFrequency;
  runAtHour: number;
  runAtMinute: number;
  runOnWeekday?: number;
  runOnDayOfMonth?: number;
  timezone: string;
  extraInstructions?: string;
};

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
}

function timezoneOptions(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "UTC", "Europe/London"];
  }
}

type Meridiem = "AM" | "PM";

function to12Hour(hour24: number): { hour12: number; meridiem: Meridiem } {
  return {
    hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
    meridiem: hour24 < 12 ? "AM" : "PM",
  };
}

function to24Hour(hour12: number, meridiem: Meridiem): number {
  if (meridiem === "AM") return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

function timeLabel(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

function recurrenceLabel(s: ScheduleRow): string {
  const at = `at ${timeLabel(s.runAtHour, s.runAtMinute)} (${s.timezone})`;
  if (s.frequency === ChatSkillScheduleFrequency.Weekly) {
    return `Weekly on ${WEEKDAYS[s.runOnWeekday ?? 0]} ${at}`;
  }
  if (s.frequency === ChatSkillScheduleFrequency.Monthly) {
    return `Monthly on day ${s.runOnDayOfMonth} ${at}`;
  }
  if (s.frequency === ChatSkillScheduleFrequency.Weekdays) {
    return `Every weekday ${at}`;
  }
  return `Daily ${at}`;
}

function extractGqlMessage(error: unknown): string {
  const e = error as any;
  return (
    e?.graphQLErrors?.[0]?.message ?? e?.message ?? "An unexpected error occurred."
  );
}

function toInput(values: ScheduleFormValues, enabled: boolean) {
  return {
    chatSkillId: values.chatSkillId,
    frequency: values.frequency,
    runAtHour: values.runAtHour,
    runAtMinute: values.runAtMinute,
    runOnWeekday:
      values.frequency === ChatSkillScheduleFrequency.Weekly
        ? values.runOnWeekday
        : null,
    runOnDayOfMonth:
      values.frequency === ChatSkillScheduleFrequency.Monthly
        ? values.runOnDayOfMonth
        : null,
    timezone: values.timezone,
    extraInstructions: values.extraInstructions || null,
    enabled,
  };
}

function ScheduleFormModal({
  open,
  onCancel,
  onSubmit,
  loading,
  initial,
  skills,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: ScheduleFormValues) => void;
  loading: boolean;
  initial?: Partial<ScheduleFormValues>;
  skills: { id: number; name: string }[];
}) {
  const [form] = Form.useForm<
    Omit<ScheduleFormValues, "runAtHour"> & { hour12: number; meridiem: Meridiem }
  >();
  const frequency = Form.useWatch("frequency", form);
  const { hour12: initialHour12, meridiem: initialMeridiem } = to12Hour(
    initial?.runAtHour ?? 8
  );

  const handleFinish = (values: {
    hour12: number;
    meridiem: Meridiem;
    [key: string]: any;
  }) => {
    const { hour12, meridiem, ...rest } = values;
    onSubmit({
      ...rest,
      runAtHour: to24Hour(hour12, meridiem),
    } as ScheduleFormValues);
  };

  return (
    <Modal
      title={initial ? "Edit Schedule" : "New Schedule"}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      destroyOnClose
      width="min(92vw, 900px)"
    >
      <Form
        form={form}
        layout="vertical"
        size="small"
        initialValues={{
          frequency: ChatSkillScheduleFrequency.Daily,
          runAtMinute: 0,
          timezone: browserTimezone(),
          ...initial,
          hour12: initialHour12,
          meridiem: initialMeridiem,
        }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="chatSkillId"
          label="Skill"
          rules={[{ required: true, message: "Pick a skill to run" }]}
        >
          <Select
            showSearch
            placeholder="Select a skill"
            options={skills.map(s => ({ value: s.id, label: `/${s.name}` }))}
          />
        </Form.Item>
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item
            name="frequency"
            label="Frequency"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select
              style={{ width: "100%" }}
              options={[
                { value: ChatSkillScheduleFrequency.Daily, label: "Daily" },
                {
                  value: ChatSkillScheduleFrequency.Weekdays,
                  label: "Every weekday (Mon-Fri)",
                },
                { value: ChatSkillScheduleFrequency.Weekly, label: "Weekly" },
                { value: ChatSkillScheduleFrequency.Monthly, label: "Monthly" },
              ]}
            />
          </Form.Item>
          {frequency === ChatSkillScheduleFrequency.Weekly && (
            <Form.Item
              name="runOnWeekday"
              label="Day of week"
              rules={[{ required: true, message: "Pick a weekday" }]}
              style={{ flex: 1 }}
            >
              <Select
                style={{ width: "100%" }}
                options={WEEKDAYS.map((d, i) => ({ value: i, label: d }))}
              />
            </Form.Item>
          )}
          {frequency === ChatSkillScheduleFrequency.Monthly && (
            <Form.Item
              name="runOnDayOfMonth"
              label="Day of month (1-28)"
              rules={[{ required: true, message: "Pick a day of the month" }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} max={28} style={{ width: "100%" }} />
            </Form.Item>
          )}
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item label="Time" required style={{ flex: "0 0 auto" }}>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Form.Item
                name="hour12"
                noStyle
                rules={[{ required: true, message: "Pick an hour" }]}
              >
                <Select
                  style={{ width: 64 }}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    value: i + 1,
                    label: String(i + 1),
                  }))}
                />
              </Form.Item>
              <Text style={{ fontSize: 14 }}>:</Text>
              <Form.Item
                name="runAtMinute"
                noStyle
                rules={[{ required: true, message: "Pick a minute" }]}
              >
                <Select
                  style={{ width: 72 }}
                  options={Array.from({ length: 12 }, (_, i) => {
                    const m = i * 5;
                    return { value: m, label: m.toString().padStart(2, "0") };
                  })}
                />
              </Form.Item>
              <Form.Item
                name="meridiem"
                noStyle
                rules={[{ required: true, message: "AM or PM" }]}
              >
                <Select
                  style={{ width: 72 }}
                  options={[
                    { value: "AM", label: "AM" },
                    { value: "PM", label: "PM" },
                  ]}
                />
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item
            name="timezone"
            label="Timezone"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select
              showSearch
              options={timezoneOptions().map(tz => ({ value: tz, label: tz }))}
            />
          </Form.Item>
        </div>
        <Form.Item
          name="extraInstructions"
          label="Additional instructions"
          extra="Optional — appended to the skill's instructions on every scheduled run"
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g. Email the result to me as a PDF summary"
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const REFETCH_SCHEDULES = {
  refetchQueries: ["MyChatSkillSchedules"],
  awaitRefetchQueries: true,
};

export function SchedulesTab() {
  const { data, loading } = useQuery(MY_CHAT_SKILL_SCHEDULES, {
    fetchPolicy: "cache-and-network",
  });
  const { data: skillsData } = useQuery(MY_ACTIVE_CHAT_SKILLS, {
    fetchPolicy: "cache-and-network",
  });
  const [addSchedule, { loading: adding }] = useMutation(
    ADD_CHAT_SKILL_SCHEDULE,
    REFETCH_SCHEDULES
  );
  const [updateSchedule, { loading: updating }] = useMutation(
    UPDATE_CHAT_SKILL_SCHEDULE,
    REFETCH_SCHEDULES
  );
  const [deleteSchedule] = useMutation(
    DELETE_CHAT_SKILL_SCHEDULE,
    REFETCH_SCHEDULES
  );
  const [runScheduleNow] = useMutation(RUN_CHAT_SKILL_SCHEDULE_NOW);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleRow | null>(null);
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());
  const [runningIds, setRunningIds] = useState<Set<number>>(new Set());

  const handleRunNow = async (schedule: ScheduleRow) => {
    setRunningIds(prev => new Set(prev).add(schedule.id));
    try {
      await runScheduleNow({ variables: { id: schedule.id } });
      notify({
        severity: "success",
        message: "Run started — check back shortly for the result.",
      });
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    } finally {
      setRunningIds(prev => {
        const next = new Set(prev);
        next.delete(schedule.id);
        return next;
      });
    }
  };

  if (loading && !data) return <CircularProgress size={16} />;

  const schedules: ScheduleRow[] =
    (data as any)?.chatSkillScheduleList?.items ?? [];
  const skills: { id: number; name: string }[] =
    (skillsData as any)?.myActiveChatSkills ?? [];
  const skillName = (id: number) =>
    skills.find(s => s.id === id)?.name ?? `skill #${id}`;

  const handleCreate = async (values: ScheduleFormValues) => {
    try {
      await addSchedule({ variables: { input: toInput(values, true) } });
      setFormOpen(false);
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  const handleUpdate = async (values: ScheduleFormValues) => {
    if (!editing) return;
    try {
      await updateSchedule({
        variables: {
          id: editing.id,
          input: toInput(values, editing.enabled),
        },
      });
      setEditing(null);
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  const handleToggle = async (schedule: ScheduleRow, enabled: boolean) => {
    setTogglingIds(prev => new Set(prev).add(schedule.id));
    try {
      await updateSchedule({
        variables: {
          id: schedule.id,
          input: toInput(
            {
              chatSkillId: schedule.chatSkillId,
              frequency: schedule.frequency,
              runAtHour: schedule.runAtHour,
              runAtMinute: schedule.runAtMinute,
              runOnWeekday: schedule.runOnWeekday ?? undefined,
              runOnDayOfMonth: schedule.runOnDayOfMonth ?? undefined,
              timezone: schedule.timezone,
              extraInstructions: schedule.extraInstructions ?? undefined,
            },
            enabled
          ),
        },
      });
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(schedule.id);
        return next;
      });
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          {schedules.length} schedule{schedules.length !== 1 ? "s" : ""}
        </Text>
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onClick={() => setFormOpen(true)}
        >
          New
        </Button>
      </div>
      {schedules.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No scheduled runs yet. Schedule a skill to run automatically — its
          results appear in your chat history.
        </Text>
      ) : (
        <List
          size="small"
          dataSource={schedules}
          renderItem={(schedule: ScheduleRow) => (
            <List.Item style={{ padding: "4px 0" }}>
              <div style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Text
                    ellipsis
                    style={{ fontSize: 12, fontWeight: 500, flex: 1 }}
                  >
                    /{skillName(schedule.chatSkillId)}
                  </Text>
                  {schedule.lastRunStatus && (
                    <Tooltip
                      title={
                        schedule.lastRunAt
                          ? `Last run ${new Date(schedule.lastRunAt).toLocaleString()}`
                          : undefined
                      }
                    >
                      <Tag
                        color={
                          schedule.lastRunStatus ===
                          ChatSkillScheduleRunStatus.Success
                            ? "green"
                            : "red"
                        }
                        style={{
                          fontSize: 9,
                          lineHeight: "12px",
                          margin: 0,
                          padding: "1px 3px",
                        }}
                      >
                        {schedule.lastRunStatus.toLowerCase()}
                      </Tag>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      schedule.enabled
                        ? "Run now, without waiting for the schedule"
                        : "Enable the schedule to run it"
                    }
                  >
                    <CaretRightOutlined
                      onClick={() =>
                        schedule.enabled &&
                        !runningIds.has(schedule.id) &&
                        handleRunNow(schedule)
                      }
                      style={{
                        fontSize: 14,
                        color: schedule.enabled ? "#999" : "#ddd",
                        cursor: schedule.enabled ? "pointer" : "not-allowed",
                        opacity: runningIds.has(schedule.id) ? 0.5 : 1,
                      }}
                      data-testid={`schedule-run-now-${schedule.id}`}
                    />
                  </Tooltip>
                  <EditOutlined
                    onClick={() => setEditing(schedule)}
                    style={{ fontSize: 12, color: "#999", cursor: "pointer" }}
                  />
                  <Popconfirm
                    title="Delete this schedule?"
                    onConfirm={async () => {
                      try {
                        await deleteSchedule({
                          variables: { id: schedule.id },
                        });
                      } catch (e) {
                        notify({ severity: "error", message: extractGqlMessage(e) });
                      }
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteOutlined
                      style={{ fontSize: 12, color: "#999", cursor: "pointer" }}
                    />
                  </Popconfirm>
                  <Switch
                    size="small"
                    checked={schedule.enabled}
                    loading={togglingIds.has(schedule.id)}
                    disabled={togglingIds.has(schedule.id)}
                    onChange={(checked: boolean) =>
                      handleToggle(schedule, checked)
                    }
                    data-testid={`schedule-toggle-${schedule.id}`}
                  />
                </div>
                <Text
                  type="secondary"
                  ellipsis
                  style={{ fontSize: 10, display: "block" }}
                >
                  {recurrenceLabel(schedule)}
                </Text>
                {schedule.enabled && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 10, color: "#bbb", display: "block" }}
                  >
                    Next run {new Date(schedule.nextRunAt).toLocaleString()}
                  </Text>
                )}
              </div>
            </List.Item>
          )}
        />
      )}
      <ScheduleFormModal
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleCreate}
        loading={adding}
        skills={skills}
      />
      <ScheduleFormModal
        key={editing?.id ?? "edit"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onSubmit={handleUpdate}
        loading={updating}
        skills={skills}
        initial={
          editing
            ? {
                chatSkillId: editing.chatSkillId,
                frequency: editing.frequency,
                runAtHour: editing.runAtHour,
                runAtMinute: editing.runAtMinute,
                runOnWeekday: editing.runOnWeekday ?? undefined,
                runOnDayOfMonth: editing.runOnDayOfMonth ?? undefined,
                timezone: editing.timezone,
                extraInstructions: editing.extraInstructions ?? undefined,
              }
            : undefined
        }
      />
    </div>
  );
}
